import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import { listCategories } from "@/infrastructure/category/categoryService";
import type { Product, TypeUnit, Category } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

interface FieldErrors {
  code?: string;
  name?: string;
  typeUnitId?: string;
}

export function ProductFormModal({
  item,
  onSave,
  onClose,
}: {
  item: Product | null;
  onSave: (data: { code: string; name: string; description?: string; typeUnitId: string; categoryId?: string }) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const isEditing = !!item;
  const [code, setCode] = useState(item?.code ?? "");
  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [typeUnitId, setTypeUnitId] = useState(item?.typeUnitId ?? "");
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? "");
  const [units, setUnits] = useState<TypeUnit[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => { listTypeUnits().then(all => setUnits(all.filter(u => u.status === "ACTIVE"))).catch(() => {}); }, []);
  useEffect(() => { listCategories({ limit: 100, status: "ACTIVE" }).then(res => setCategories(res.items.filter(c => !c.isSystem))).catch(() => {}); }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FieldErrors = {};
    if (!isEditing && !code.trim()) errors.code = t.products.errorFieldRequired;
    if (!name.trim()) errors.name = t.products.errorFieldRequired;
    if (!isEditing && !typeUnitId) errors.typeUnitId = t.products.errorFieldRequired;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    onSave({ code: code.trim(), name: name.trim(), description: description.trim() || undefined, typeUnitId, categoryId: categoryId || undefined });
  };

  const unitName = units.find(u => u.id === item?.typeUnitId);

  const isFormValid = isEditing
    ? !!name.trim()
    : !!code.trim() && !!name.trim() && !!typeUnitId;

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {isEditing ? t.products.editProduct : t.products.addProduct}
        </h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Field label={t.products.codeLabel} value={code} onChange={setCode} placeholder={t.products.codePlaceholder} disabled={isEditing} error={fieldErrors.code} required={!isEditing} />
          <Field label={t.products.nameLabel} value={name} onChange={setName} placeholder={t.products.namePlaceholder} error={fieldErrors.name} required />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.products.descriptionLabel}</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t.products.descriptionPlaceholder} rows={3} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box", resize: "vertical" }} />
          </div>

          {isEditing ? (
            <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#aaa" }}>
              {t.products.unitLabel}: <strong style={{ color: "#555" }}>{unitName?.name ?? item?.typeUnitId}</strong> ({unitName?.abbreviation ?? ""})
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.products.unitLabel} <span style={{ color: colors.error }}>*</span></label>
              <select value={typeUnitId} onChange={e => setTypeUnitId(e.target.value)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: typeUnitId ? "#111" : "#aaa", background: "#fff", border: `1.5px solid ${fieldErrors.typeUnitId ? colors.error : "#e5e5e5"}`, borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }}>
                <option value="">{t.products.unitPlaceholder}</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>)}
              </select>
              {fieldErrors.typeUnitId && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{fieldErrors.typeUnitId}</span>}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.products.categoryLabel}</label>
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }}>
              <option value="">{t.products.categoryPlaceholder}</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.products.cancel}</button>
            <button type="submit" disabled={!isFormValid} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: isFormValid ? "#111" : "#aaa", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: isFormValid ? "pointer" : "not-allowed", transition: `background ${transitions.fast}`, opacity: isFormValid ? 1 : 0.6 }} onMouseEnter={e => { if (isFormValid) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }} onMouseLeave={e => { if (isFormValid) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}>{t.products.save}</button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, disabled = false, error, required = false }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; disabled?: boolean; error?: string; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label} {required && <span style={{ color: colors.error }}>*</span>}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: disabled ? "#f5f5f5" : "#fff", border: `1.5px solid ${error ? colors.error : "#e5e5e5"}`, borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box", opacity: disabled ? 0.6 : 1 }} />
      {error && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{error}</span>}
    </div>
  );
}
